// src/logic/loginHelpers.ts
import { trackPromise } from "react-promise-tracker";
import { validEmail, validPassword } from "./validation";
import { getUserDetails, loginService } from "../../../services/login.service";
import store from "../../../store";
import {
  setDisplayErrorSnackBar,
  setUserData,
  setUserToken,
} from "../../../features/userSlice";
import { authService } from "../../../logic/authService";

const isValidateFields = (email: string, password: string): boolean => {
  return validEmail(email) === "" && validPassword(password) === "";
};

const handleTokenExpired = (res: any, props: any) => {
  props.dispatch(
    setDisplayErrorSnackBar({
      state: true,
      message: res?.text || "Session expired",
    })
  );
  props.setUserSignInValueLocalStorage?.("false");
  localStorage.setItem("signin", "false");
  authService.clearToken();
};

const isSuccessLogin = async (body: any, props: any) => {
  try {
    const result = await trackPromise(
      (async () => {
        try {
          const res = await loginService.signIn(body);

          // New backend returns access_token; refresh token is in httpOnly cookie
          if (res?.data?.access_token) {
            // Save access token in Redux/in-memory
            authService.setToken(res?.data?.access_token);
            authService.setAccessTokenExpiration(
              res?.data?.access_token_expires_at
            );
            try {
              const me = await getUserDetails(res.data.access_token);
              props.dispatch(setUserToken(res?.data?.access_token));

              if (me?.data) {
                // Wait for Redux to update before navigating
                await props.dispatch(setUserData({ ...me }));
                store.dispatch(
                  setDisplayErrorSnackBar({ state: false, message: "" })
                );

                // Additional delay for state propagation
                await new Promise((resolve) => setTimeout(resolve, 100));
              } else {
                props.setLoginResponse?.({ success: false, status: 500 });
                return { success: false, status: 500 };
              }
            } catch (e) {
              props.setLoginResponse?.({ success: false, status: 500 });
              props.handleSnackbarOpen?.();
              props.setSnackbarMessage?.(
                "Failed to load user data. Please try again."
              );
              return { success: false, status: 500 };
            }

            props.setLoginResponse?.({ success: true, status: 200 });

            return { success: true };
          }

          props.setLoginResponse?.({ success: false, status: 200 });
          return { success: false, status: 200 };
        } catch (error: any) {
          console.error("loginHelpers:isSuccessLogin error", error);
          const status = error?.response?.status || error?.status;

          props.setLoginResponse?.({ success: false, status });

          switch (status) {
            case 400:
              props.handleSnackbarOpen?.();
              props.setSnackbarMessage?.(
                "Invalid email or password. Please check your credentials and try again."
              );
              handleTokenExpired(error, props);
              break;

            case 403:
              props.handleSnackbarOpen?.();
              props.setSnackbarMessage?.(
                "Password expired. Please reset your password."
              );
              handleTokenExpired(error, props);
              break;

            case 401:
              props.handleSnackbarOpen?.();
              props.setSnackbarMessage?.(
                "Login failed. Please check your email and password and try again."
              );
              handleTokenExpired(error, props);
              break;

            default:
              props.handleSnackbarOpen?.();
              props.setSnackbarMessage?.(
                "Login failed. Please try again later."
              );
              try {
                store.dispatch(
                  setDisplayErrorSnackBar({
                    state: true,
                    message: "Login failed. Please try again later.",
                  })
                );
              } catch {}
              break;
          }

          return { success: false, status };
        }
      })()
    );
    return result;
  } catch (e: any) {
    try {
      store.dispatch(
        setDisplayErrorSnackBar({ state: true, message: e?.message || e })
      );
    } catch {}
    return { success: false, status: 500 };
  }
};

export const loginHelpers = {
  isSuccessLogin,
  isValidateFields,
};
